import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Offcanvas, Form, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import {
  Shield,
  Key,
  Plus,
  Minus,
  RotateCcw,
  Check,
  Loader2,
  ChevronRight,
  ChevronDown,
  Lock,
} from 'lucide-react';

import { cn } from '../../lib/utils';
import { USER_TYPE_TO_GROUP, getIdentityRole } from '../../lib/rbacConstants';
import {
  useFetchRoles,
  useFetchAllPermissions,
  useFetchUserRoles,
  useSetUserRoles,
  useFetchUserPermissionOverrides,
  useSetUserPermissionOverrides,
} from '../../services/roles';
import usePermission from '../../hooks/usePermission';

const PROTECTED_ROLES = ['admin'];

const CATEGORY_CONFIG = {
  claims:   { label: 'Claims',   color: 'blue' },
  users:    { label: 'Users',    color: 'cyan' },
  files:    { label: 'Files',    color: 'slate' },
  reports:  { label: 'Reports',  color: 'amber' },
  support:  { label: 'Support',  color: 'emerald' },
  admin:    { label: 'System',   color: 'red' },
  provider: { label: 'Provider', color: 'blue' },
};

const UserRolePermissionsPanel = ({ show, onHide, user, onSuccess }) => {
  const queryClient = useQueryClient();
  const { is: hasRole } = usePermission();

  // State
  const [activeTab, setActiveTab] = useState('roles');
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [initialRoles, setInitialRoles] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [initialOverrides, setInitialOverrides] = useState({});

  // Permissions
  const canAssignProtected = hasRole('admin');

  // Data fetching
  const { data: allRoles, isLoading: rolesLoading, error: rolesError } = useFetchRoles();
  const { data: userRoles, isLoading: userRolesLoading } = useFetchUserRoles(user?.userId);
  const { data: allPermissions, isLoading: permissionsLoading } = useFetchAllPermissions();
  const { data: userPermissionData, isLoading: userPermissionsLoading } = useFetchUserPermissionOverrides(user?.userId);

  // Group filtering — only show roles/permissions compatible with the target user's type
  const userGroup = user?.userType ? USER_TYPE_TO_GROUP[user.userType] : null;

  const filteredRoles = useMemo(() => {
    if (!allRoles || !userGroup) return allRoles || [];
    return allRoles.filter(r => r.roleGroup === userGroup);
  }, [allRoles, userGroup]);

  const groupFilteredPermissions = useMemo(() => {
    if (!allPermissions || !userGroup) return allPermissions || [];
    return allPermissions.filter(p =>
      p.permissionGroups && p.permissionGroups.includes(userGroup)
    );
  }, [allPermissions, userGroup]);

  // Mutations
  const setUserRoles = useSetUserRoles(
    () => {
      toast.success('Roles updated successfully');
      queryClient.invalidateQueries(['user-roles', user.userId]);
      queryClient.invalidateQueries(['role-management-users']);
      queryClient.invalidateQueries(['admins']);
      setInitialRoles([...selectedRoles]);
      if (onSuccess) onSuccess();
    },
    {
      onError: (error) => {
        toast.error(error.response?.data?.msg || error.message || 'Failed to update roles');
      },
    }
  );

  const setUserOverrides = useSetUserPermissionOverrides(
    () => {
      toast.success('Permissions updated successfully');
      queryClient.invalidateQueries(['user-permission-overrides', user.userId]);
      queryClient.invalidateQueries(['role-management-users']);
      setInitialOverrides({ ...overrides });
      if (onSuccess) onSuccess();
    },
    {
      onError: (error) => {
        toast.error(error.response?.data?.msg || error.message || 'Failed to update permissions');
      },
    }
  );

  // Reset state when user changes
  useEffect(() => {
    if (user?.userId) {
      setActiveTab('roles');
      setSelectedRoles([]);
      setInitialRoles([]);
      setOverrides({});
      setInitialOverrides({});
    }
  }, [user?.userId]);

  // Initialize roles from data (always include identity role)
  useEffect(() => {
    if (userRoles) {
      const roleNames = userRoles.map((r) => r.roleName);
      const identity = user?.userType ? getIdentityRole(user.userType) : null;
      if (identity && !roleNames.includes(identity)) {
        roleNames.push(identity);
      }
      setSelectedRoles(roleNames);
      setInitialRoles(roleNames);
    }
  }, [userRoles, user?.userType]);

  // Initialize overrides from data
  useEffect(() => {
    if (userPermissionData?.overrides) {
      const overridesMap = {};
      userPermissionData.overrides.forEach((override) => {
        overridesMap[override.permissionKey] = override.overrideType;
      });
      setOverrides(overridesMap);
      setInitialOverrides(overridesMap);
    }
  }, [userPermissionData]);

  // Role permissions lookup (permissions that come from roles) - rolePermissions is string[]
  const rolePermissionsSet = useMemo(() => {
    if (!userPermissionData?.rolePermissions) return new Set();
    return new Set(userPermissionData.rolePermissions);
  }, [userPermissionData]);

  // Check if own admin role
  const isOwnAdminRole = useCallback(
    (roleName) => {
      return PROTECTED_ROLES.includes(roleName) && user?.isSelf;
    },
    [user?.isSelf]
  );

  // Check if can modify a role
  const canModifyRole = useCallback(
    (roleName) => {
      if (PROTECTED_ROLES.includes(roleName)) {
        return canAssignProtected;
      }
      return true;
    },
    [canAssignProtected]
  );

  // Handle role change
  const handleRoleChange = useCallback((roleName, checked) => {
    setSelectedRoles((prev) => {
      if (checked) {
        return [...prev, roleName];
      } else {
        return prev.filter((r) => r !== roleName);
      }
    });
  }, []);

  // Changes detection
  const hasRolesChanges = useMemo(() => {
    if (selectedRoles.length !== initialRoles.length) return true;
    const sortedSelected = [...selectedRoles].sort();
    const sortedInitial = [...initialRoles].sort();
    return sortedSelected.some((role, idx) => role !== sortedInitial[idx]);
  }, [selectedRoles, initialRoles]);

  const hasPermissionsChanges = useMemo(() => {
    const currentKeys = Object.keys(overrides);
    const initialKeys = Object.keys(initialOverrides);
    if (currentKeys.length !== initialKeys.length) return true;
    return currentKeys.some((key) => overrides[key] !== initialOverrides[key]);
  }, [overrides, initialOverrides]);

  const hasAnyChanges = activeTab === 'roles' ? hasRolesChanges : hasPermissionsChanges;

  // Permission stats (uses group-filtered permissions)
  const permissionStats = useMemo(() => {
    if (!groupFilteredPermissions.length) return { effective: 0, fromRoles: 0, granted: 0, revoked: 0 };

    const filteredKeys = new Set(groupFilteredPermissions.map(p => p.permissionKey));
    const fromRoles = [...rolePermissionsSet].filter(k => filteredKeys.has(k)).length;
    let granted = 0;
    let revoked = 0;

    Object.entries(overrides).forEach(([key, type]) => {
      if (!filteredKeys.has(key)) return;
      if (type === 'grant') granted++;
      if (type === 'revoke') revoked++;
    });

    const effective = fromRoles + granted - revoked;

    return { effective, fromRoles, granted, revoked };
  }, [groupFilteredPermissions, rolePermissionsSet, overrides]);

  // Group permissions by category (uses group-filtered permissions)
  const permissionsByCategory = useMemo(() => {
    if (!groupFilteredPermissions.length) return {};

    const grouped = {};
    groupFilteredPermissions.forEach((perm) => {
      const category = perm.permissionCategory || 'other';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(perm);
    });

    return grouped;
  }, [groupFilteredPermissions]);

  // Permission actions
  const clearAllOverrides = useCallback(() => {
    setOverrides({});
  }, []);

  const grantAll = useCallback(() => {
    if (!groupFilteredPermissions.length) return;
    const newOverrides = {};
    groupFilteredPermissions.forEach((perm) => {
      // Only grant permissions not already from roles
      if (!rolePermissionsSet.has(perm.permissionKey)) {
        newOverrides[perm.permissionKey] = 'grant';
      }
    });
    setOverrides(newOverrides);
  }, [groupFilteredPermissions, rolePermissionsSet]);

  const revokeAll = useCallback(() => {
    // Only revoke permissions that come from roles
    const newOverrides = {};
    rolePermissionsSet.forEach((permKey) => {
      newOverrides[permKey] = 'revoke';
    });
    setOverrides(newOverrides);
  }, [rolePermissionsSet]);

  // Toggle permission
  const togglePermission = useCallback(
    (permissionKey) => {
      const fromRole = rolePermissionsSet.has(permissionKey);
      const currentOverride = overrides[permissionKey];

      setOverrides((prev) => {
        const newOverrides = { ...prev };

        if (fromRole) {
          // Permission comes from role
          if (currentOverride === 'revoke') {
            // Remove revoke override (restore role permission)
            delete newOverrides[permissionKey];
          } else {
            // Add revoke override
            newOverrides[permissionKey] = 'revoke';
          }
        } else {
          // Permission does not come from role
          if (currentOverride === 'grant') {
            // Remove grant override
            delete newOverrides[permissionKey];
          } else {
            // Add grant override
            newOverrides[permissionKey] = 'grant';
          }
        }

        return newOverrides;
      });
    },
    [rolePermissionsSet, overrides]
  );

  // Get permission effective state
  const getPermissionState = useCallback(
    (permissionKey) => {
      const fromRole = rolePermissionsSet.has(permissionKey);
      const override = overrides[permissionKey];

      if (override === 'grant') {
        return { enabled: true, source: 'granted', fromRole: false };
      }
      if (override === 'revoke') {
        return { enabled: false, source: 'revoked', fromRole: false };
      }
      if (fromRole) {
        return { enabled: true, source: 'role', fromRole: true };
      }
      return { enabled: false, source: null, fromRole: false };
    },
    [rolePermissionsSet, overrides]
  );

  // Save handlers
  const handleSave = useCallback(() => {
    if (activeTab === 'roles') {
      setUserRoles.mutate({
        userId: user.userId,
        roleNames: selectedRoles,
      });
    } else {
      // Convert overrides object to array format
      const overrideArray = Object.entries(overrides).map(([permissionKey, overrideType]) => ({
        permissionKey,
        overrideType,
      }));

      setUserOverrides.mutate({
        userId: user.userId,
        overrides: overrideArray,
      });
    }
  }, [activeTab, user?.userId, selectedRoles, overrides, setUserRoles, setUserOverrides]);

  const isSaving = setUserRoles.isLoading || setUserOverrides.isLoading;

  // User initials
  const userInitials = useMemo(() => {
    if (!user) return '';
    const first = user.userFirstName?.[0] || '';
    const last = user.userLastName?.[0] || '';
    return (first + last).toUpperCase();
  }, [user]);

  // Loading state
  const isLoading =
    activeTab === 'roles'
      ? rolesLoading || userRolesLoading
      : permissionsLoading || userPermissionsLoading;

  // Error state
  const error = activeTab === 'roles' ? rolesError : null;

  if (!user) return null;

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      style={{ width: '500px' }}
      className="d-flex flex-column"
    >
      <Offcanvas.Header closeButton className="border-bottom">
        <div className="d-flex align-items-center gap-3 w-100">
          <div className="h-10 w-10 rounded-full bg-slate-700 text-white flex items-center justify-center text-sm font-medium">
            {userInitials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="d-flex align-items-center gap-2">
              <h5 className="mb-0 font-semibold">
                {user.userFirstName} {user.userLastName}
              </h5>
              {user.isSelf && (
                <span className="bg-blue-100 text-blue-700 text-xs rounded-full px-2 py-0.5">
                  You
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500 mb-0">{user.userEmail}</p>
          </div>
        </div>
      </Offcanvas.Header>

      {/* Tab bar */}
      <div className="px-4 pt-3 pb-0">
        <div className="inline-flex rounded-lg bg-slate-100 p-1">
          <button
            onClick={() => setActiveTab('roles')}
            className={cn(
              'px-4 py-1.5 text-sm font-medium transition-all rounded-md',
              activeTab === 'roles'
                ? 'bg-white shadow-sm text-slate-900'
                : 'text-slate-500 hover:text-slate-700'
            )}
          >
            <Shield className="h-4 w-4 inline-block mr-1.5 -mt-0.5" />
            Roles
          </button>
          <button
            onClick={() => setActiveTab('permissions')}
            className={cn(
              'px-4 py-1.5 text-sm font-medium transition-all rounded-md',
              activeTab === 'permissions'
                ? 'bg-white shadow-sm text-slate-900'
                : 'text-slate-500 hover:text-slate-700'
            )}
          >
            <Key className="h-4 w-4 inline-block mr-1.5 -mt-0.5" />
            Permissions
          </button>
        </div>
      </div>

      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Permissions stats/actions - pinned above scroll */}
        {!isLoading && !error && activeTab === 'permissions' && (
          <PermissionsToolbar
            stats={permissionStats}
            overrides={overrides}
            onClearAll={clearAllOverrides}
            onGrantAll={grantAll}
            onRevokeAll={revokeAll}
          />
        )}

        <Offcanvas.Body className="p-0 flex-1 overflow-y-auto">
          {isLoading && (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          )}

          {error && (
            <div className="mx-4 my-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800 mb-0">
                {error.message || 'Failed to load data'}
              </p>
            </div>
          )}

          {!isLoading && !error && activeTab === 'roles' && (
            <RolesView
              allRoles={filteredRoles}
              selectedRoles={selectedRoles}
              onRoleChange={handleRoleChange}
              canAssignProtected={canAssignProtected}
              canModifyRole={canModifyRole}
              isOwnAdminRole={isOwnAdminRole}
              allPermissions={groupFilteredPermissions}
              userType={user?.userType}
            />
          )}

          {!isLoading && !error && activeTab === 'permissions' && (
            <PermissionsAccordion
              permissionsByCategory={permissionsByCategory}
              onTogglePermission={togglePermission}
              getPermissionState={getPermissionState}
            />
          )}
        </Offcanvas.Body>

        {/* Footer */}
        <div className="border-t border-slate-200 bg-white px-4 py-3 flex items-center justify-between">
          {hasAnyChanges && (
            <div className="flex items-center gap-2 text-sm text-amber-600">
              <span className="h-2 w-2 rounded-full bg-amber-500" />
              Unsaved changes
            </div>
          )}
          {!hasAnyChanges && <div />}
          <button
            onClick={handleSave}
            disabled={!hasAnyChanges || isSaving}
            className={cn(
              'inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              hasAnyChanges && !isSaving
                ? 'hover:bg-blue-700'
                : 'opacity-50 cursor-not-allowed'
            )}
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4" />
            )}
            {activeTab === 'roles' ? 'Save Roles' : 'Save Permissions'}
          </button>
        </div>
      </div>
    </Offcanvas>
  );
};

// RolesView Component
const RolesView = ({
  allRoles,
  selectedRoles,
  onRoleChange,
  canAssignProtected,
  canModifyRole,
  isOwnAdminRole,
  allPermissions,
  userType,
}) => {
  const [expandedRole, setExpandedRole] = useState(null);
  const hasProtectedRoles = allRoles?.some((r) => PROTECTED_ROLES.includes(r.roleName));
  const identityRoleName = userType ? getIdentityRole(userType) : null;

  // Build permission lookup map (key → { displayName, category })
  const permissionLookup = useMemo(() => {
    if (!allPermissions) return {};
    const map = {};
    allPermissions.forEach(p => {
      map[p.permissionKey] = {
        displayName: p.permissionDisplayName,
        category: p.permissionCategory,
      };
    });
    return map;
  }, [allPermissions]);

  return (
    <div className="px-4 py-2">
      <p className="text-sm text-slate-500 mb-4">
        Select roles to assign. Users can have multiple roles.
      </p>

      {!canAssignProtected && hasProtectedRoles && (
        <div className="flex items-start gap-2 p-3 mb-4 rounded-lg bg-amber-50 border border-amber-200">
          <Shield className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-sm text-amber-800 mb-0">
            Protected roles can only be assigned by administrators.
          </p>
        </div>
      )}

      <div className="divide-y divide-slate-100">
        {allRoles?.map((role) => {
          const isProtected = PROTECTED_ROLES.includes(role.roleName);
          const canModify = canModifyRole(role.roleName);
          const isChecked = selectedRoles.includes(role.roleName);
          const isIdentityLocked = role.roleName === identityRoleName;
          const isDisabled = !canModify || isOwnAdminRole(role.roleName) || isIdentityLocked;

          return (
            <React.Fragment key={role.roleId}>
              <div
                onClick={() => setExpandedRole(prev => prev === role.roleName ? null : role.roleName)}
                className={cn(
                  'flex items-start justify-between py-2.5 px-1 transition-colors cursor-pointer',
                  isChecked ? 'bg-blue-50/50' : 'hover:bg-slate-50',
                  isDisabled && 'opacity-50'
                )}
              >
                <div className="flex items-center gap-2 flex-1 min-w-0 mr-3">
                  <ChevronRight
                    className={cn(
                      'h-4 w-4 text-slate-400 transition-transform duration-200 shrink-0',
                      expandedRole === role.roleName && 'rotate-90'
                    )}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{role.roleDisplayName}</span>
                      {isProtected && (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">
                          <Shield className="h-3 w-3" /> Protected
                        </span>
                      )}
                      {isIdentityLocked && (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-700 bg-slate-200 rounded-full px-2 py-0.5">
                          <Lock className="h-3 w-3" /> Identity
                        </span>
                      )}
                      {role.permissions?.length > 0 && (
                        <span className="text-xs text-slate-400 bg-slate-100 rounded-full px-2 py-0.5">
                          {role.permissions.length} permissions
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 mb-0">{role.roleDescription}</p>
                    {isOwnAdminRole(role.roleName) && (
                      <p className="text-xs text-red-600 mt-0.5 mb-0">
                        You cannot remove your own admin role.
                      </p>
                    )}
                    {isIdentityLocked && !isOwnAdminRole(role.roleName) && (
                      <p className="text-xs text-slate-500 mt-0.5 mb-0">
                        This role is locked to the user's account type.
                      </p>
                    )}
                  </div>
                </div>
                <Form.Check
                  type="switch"
                  checked={isChecked}
                  disabled={isDisabled}
                  onChange={(e) => onRoleChange(role.roleName, e.target.checked)}
                  onClick={(e) => e.stopPropagation()}
                  className="mt-0.5"
                />
              </div>
              {expandedRole === role.roleName && role.permissions?.length > 0 && (
                <div className="ml-1 mb-2 pl-3 border-l-2 border-slate-200 bg-slate-50/50 rounded-r-md py-2 pr-2">
                  {Object.entries(
                    role.permissions.reduce((groups, permKey) => {
                      const category = permissionLookup[permKey]?.category || permKey.split(':')[0];
                      if (!groups[category]) groups[category] = [];
                      groups[category].push(permKey);
                      return groups;
                    }, {})
                  ).map(([category, permKeys]) => {
                    const categoryLabel = CATEGORY_CONFIG[category]?.label || category.charAt(0).toUpperCase() + category.slice(1);
                    return (
                      <div key={category} className="mb-2 last:mb-0">
                        <p className="text-xs font-bold text-emerald-600 mb-0.5 px-1">
                          {categoryLabel}
                        </p>
                        {permKeys.map(key => (
                          <p key={key} className="text-xs text-slate-500 mb-0 py-0.5 px-1">
                            {permissionLookup[key]?.displayName || key.split(':').slice(1).join(' ').replace(/_/g, ' ')}
                          </p>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

// PermissionsView Component
// Pinned stats + action buttons (rendered outside scroll area)
const PermissionsToolbar = ({ stats, overrides, onClearAll, onGrantAll, onRevokeAll }) => {
  return (
    <div className="px-4 py-3 border-b border-slate-100 shrink-0">
      {/* Stats row - no wrap, centered */}
      <div className="flex items-center justify-center gap-3 whitespace-nowrap mb-2">
        <div className="flex items-baseline gap-1.5 text-sm">
          <span className="font-semibold text-slate-700">{stats.effective}</span>
          <span className="text-slate-400">Effective</span>
        </div>
        <span className="text-slate-200">|</span>
        <div className="flex items-baseline gap-1.5 text-sm">
          <span className="font-semibold text-blue-600">{stats.fromRoles}</span>
          <span className="text-slate-400">From Roles</span>
        </div>
        <span className="text-slate-200">|</span>
        <div className="flex items-baseline gap-1.5 text-sm">
          <span className="font-semibold text-emerald-600">+{stats.granted}</span>
          <span className="text-slate-400">Granted</span>
        </div>
        <span className="text-slate-200">|</span>
        <div className="flex items-baseline gap-1.5 text-sm">
          <span className="font-semibold text-red-600">-{stats.revoked}</span>
          <span className="text-slate-400">Revoked</span>
        </div>
      </div>

      {/* Quick actions row - no wrap, centered */}
      <div className="flex items-center justify-center gap-2 whitespace-nowrap">
        <button
          onClick={onClearAll}
          disabled={Object.keys(overrides).length === 0}
          className={cn(
            'inline-flex items-center gap-1 text-xs font-medium text-slate-600 border border-slate-200 bg-white rounded-md px-2.5 py-1 transition-colors',
            Object.keys(overrides).length > 0
              ? 'hover:bg-slate-50'
              : 'opacity-40 cursor-not-allowed'
          )}
        >
          <RotateCcw className="h-3 w-3" /> Clear Overrides
        </button>
        <button
          onClick={onGrantAll}
          className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 border border-emerald-200 bg-white hover:bg-emerald-50 rounded-md px-2.5 py-1 transition-colors"
        >
          <Plus className="h-3 w-3" /> Grant All
        </button>
        <button
          onClick={onRevokeAll}
          className="inline-flex items-center gap-1 text-xs font-medium text-red-700 border border-red-200 bg-white hover:bg-red-50 rounded-md px-2.5 py-1 transition-colors"
        >
          <Minus className="h-3 w-3" /> Revoke All
        </button>
      </div>
    </div>
  );
};

// Scrollable permission categories (rendered inside scroll area).
// Plain Tailwind collapsible (state + conditional render) — NOT react-bootstrap's
// Accordion, whose animated Collapse desyncs under React 18 StrictMode's dev
// double-render (header shows open while the body stays hidden).
const PermissionsAccordion = ({ permissionsByCategory, onTogglePermission, getPermissionState }) => {
  const [openCategories, setOpenCategories] = useState(() => new Set());

  const toggleCategory = useCallback((category) => {
    setOpenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  }, []);

  return (
    <div className="px-4">
      {Object.entries(permissionsByCategory).map(([category, permissions]) => {
        const config = CATEGORY_CONFIG[category] || {
          label: category,
          color: 'slate',
        };

        const states = permissions.map((p) => getPermissionState(p.permissionKey));
        const enabledCount = states.filter((s) => s.enabled).length;
        const grantedCount = states.filter((s) => s.source === 'granted').length;
        const revokedCount = states.filter((s) => s.source === 'revoked').length;
        const isOpen = openCategories.has(category);

        return (
          <div key={category} className="border-b border-slate-100 last:border-b-0">
            <button
              type="button"
              onClick={() => toggleCategory(category)}
              aria-expanded={isOpen}
              className="flex w-full items-center justify-between rounded-md px-1 py-2.5 text-left transition-colors hover:bg-slate-50"
            >
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium bg-slate-100 text-slate-600">
                  {config.label}
                </span>
                <span className="text-xs text-slate-500">
                  {enabledCount}/{permissions.length}
                </span>
                {grantedCount > 0 && (
                  <span className="inline-flex items-center text-xs font-medium text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5">
                    +{grantedCount}
                  </span>
                )}
                {revokedCount > 0 && (
                  <span className="inline-flex items-center text-xs font-medium text-red-700 bg-red-100 rounded-full px-2 py-0.5">
                    -{revokedCount}
                  </span>
                )}
              </div>
              <ChevronDown
                className={cn(
                  'h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200',
                  isOpen && 'rotate-180'
                )}
              />
            </button>

            {isOpen && (
              <div className="pb-3 pt-1">
                <div className="divide-y divide-slate-100">
                  {permissions.map((permission) => (
                    <PermissionRow
                      key={permission.permissionId || permission.permissionKey}
                      permission={permission}
                      state={getPermissionState(permission.permissionKey)}
                      onToggle={onTogglePermission}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// PermissionRow Component
const PermissionRow = ({ permission, state, onToggle }) => {
  const { enabled, source } = state;

  const sourceBadge = useMemo(() => {
    if (source === 'granted') {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5">
          <Plus className="h-3 w-3" /> Granted
        </span>
      );
    }
    if (source === 'revoked') {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-100 rounded-full px-2 py-0.5">
          <Minus className="h-3 w-3" /> Revoked
        </span>
      );
    }
    if (source === 'role') {
      return (
        <span className="inline-flex items-center text-xs font-medium text-slate-600 bg-slate-100 rounded-full px-2 py-0.5">
          Role
        </span>
      );
    }
    return null;
  }, [source]);

  return (
    <div
      onClick={() => onToggle(permission.permissionKey)}
      className={cn(
        'flex items-start justify-between py-2 px-1 cursor-pointer transition-colors',
        source === 'granted' && 'bg-emerald-50/50',
        source === 'revoked' && 'bg-red-50/50',
        (!source || source === 'role') && 'hover:bg-slate-50'
      )}
    >
      <div className="flex items-start gap-2.5 flex-1 min-w-0">
        <Form.Check
          type="switch"
          checked={enabled}
          onChange={(e) => {
            e.stopPropagation();
            onToggle(permission.permissionKey);
          }}
          className="mt-0.5"
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-900 mb-0.5">
            {permission.permissionDisplayName}
          </p>
          {permission.permissionDescription && (
            <p className="text-xs text-slate-500 mb-0">
              {permission.permissionDescription}
            </p>
          )}
        </div>
      </div>
      {sourceBadge && <div className="ml-2">{sourceBadge}</div>}
    </div>
  );
};


export default UserRolePermissionsPanel;
